import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EmployerCockpitSidebar } from './EmployerCockpitSidebar';
import { ZijMenuMobielKnop, PaginaKop } from './ZijMenu';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';

interface CockpitTitleContextType {
  setTitle: (title: string, subtitle?: string) => void;
}

const CockpitTitleContext = createContext<CockpitTitleContextType>({ setTitle: () => {} });

export const useCockpitTitle = (title: string, subtitle?: string) => {
  const { setTitle } = useContext(CockpitTitleContext);
  useEffect(() => {
    setTitle(title, subtitle);
  }, [title, subtitle, setTitle]);
};

interface EmployerCockpitLayoutProps {
  children: React.ReactNode;
  /** false = geen algemene linkerkolom; de pagina vult die kolom zelf (Mijn Cockpit: stappenmenu) */
  zijbalk?: boolean;
}

export const EmployerCockpitLayout: React.FC<EmployerCockpitLayoutProps> = ({ children, zijbalk = true }) => {
  const { user, updateUser } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  const [pageSubtitle, setPageSubtitle] = useState('');

  const setTitle = useCallback((title: string, subtitle?: string) => {
    setPageTitle(title);
    setPageSubtitle(subtitle ?? '');
  }, []);

  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me').then((res) => res.data.data),
    enabled: !!user && (user.role === 'opdrachtgever' || user.role === 'employer_member'),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (meData?.user) {
      updateUser(meData.user);
    }
  }, [meData?.user, updateUser]);

  // Onboarding-gate: hoofdgebruiker met onvoltooide onboarding eerst door de wizard
  const onboardingStatus = (meData?.profile as any)?.onboarding_status;
  if (user?.role === 'opdrachtgever' && onboardingStatus && onboardingStatus !== 'company_active') {
    return <Navigate to="/employer/onboarding" replace />;
  }

  return (
    <CockpitTitleContext.Provider value={{ setTitle }}>
    <div className="min-h-screen bg-gray-50">
      {zijbalk && <ZijMenuMobielKnop onClick={() => setSidebarOpen(true)} />}
      <div className="max-w-7xl mx-auto flex h-[calc(100vh-4rem)] overflow-hidden">
        {zijbalk && (
          <EmployerCockpitSidebar
            mobileOpen={sidebarOpen}
            onMobileClose={() => setSidebarOpen(false)}
          />
        )}
        <div className="flex-1 min-w-0 overflow-y-auto bg-gray-50">
          {/* Paginatitel in de inhoudskolom (merkstijl okt 2026); signalen staan op het Dashboard */}
          {(pageTitle || pageSubtitle) && (
            <PaginaKop titel={pageTitle} ondertitel={pageSubtitle || undefined} className="px-4 sm:px-6 pt-6" />
          )}
          {children}
        </div>
      </div>
    </div>
    </CockpitTitleContext.Provider>
  );
};
